import { z } from 'zod';

import { pageInfoSchema } from '@/shared/api';

import type { MyMessage, MyMessages, SentMessage } from '../model/types';

/** Validated at the boundary, mapped straight to the entity type (§7.4). */
const audienceSchema = z.enum(['account', 'everyone', 'day']);

export const sentMessageSchema = z.object({
  id: z.string(),
  audience: audienceSchema,
  accountId: z.string().nullable(),
  recipientLabel: z.string().nullable(),
  day: z.string().nullable(),
  body: z.string(),
  sentAt: z.string().datetime({ offset: true }),
  recipientCount: z.number().int().nonnegative(),
  readCount: z.number().int().nonnegative(),
});

export const sentMessageListSchema = z.object({
  items: z.array(sentMessageSchema),
  page: pageInfoSchema,
});

const myMessageSchema = z.object({
  id: z.string(),
  body: z.string(),
  sentAt: z.string().datetime({ offset: true }),
  audience: audienceSchema,
  day: z.string().nullable(),
  isRead: z.boolean(),
  sender: z.string().nullish(),
});

export const myMessagesSchema = z.object({
  items: z.array(myMessageSchema),
  unreadCount: z.number().int().nonnegative(),
  hasMore: z.boolean().default(false),
});

export const audiencePreviewSchema = z.object({ recipientCount: z.number().int().nonnegative() });

export const sentMessageIdSchema = z.object({ id: z.string() });

export function toSentMessage(dto: z.infer<typeof sentMessageSchema>): SentMessage {
  return { ...dto, sentAt: new Date(dto.sentAt) };
}

function toMyMessage(dto: z.infer<typeof myMessageSchema>): MyMessage {
  const sender = dto.sender?.trim() ?? '';

  return { ...dto, sentAt: new Date(dto.sentAt), sender: sender === '' ? null : sender };
}

export function toMyMessages(dto: z.infer<typeof myMessagesSchema>): MyMessages {
  return { items: dto.items.map(toMyMessage), unreadCount: dto.unreadCount, hasMore: dto.hasMore };
}
